/* eslint-disable no-template-curly-in-string */
/* eslint-disable no-loop-func */
import React from 'react';
import { createContext, useContext } from 'react';
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import _ from 'lodash';
// import moment from 'moment';


import { axios } from '../../../v5/utils/axios';
import { prepareDate, saveDate, success, successAPI, errorAPI, placeholderRows, removeEmptyRows, isNumeric, hasPermission, getBillingUOMv4 } from '../../../v5/utils/utils';
import Yup from '../../../v5/utils/yup';
import { ReportUOM, Permissions } from '../../../v5/utils/enums';

import { BillingTypes } from '../jobs/slice';
import { getYupForKG, getYupForTONS, getYupForMAX } from '../jobs/form/weightChit/slice';


export const LocalGlobal = {
  LOCAL: 1,
  GLOBAL: 2,
};

export const BinWaste = {
  BIN: 1,
  WASTE: 2,
};


export const fields = {
  serviceTag: {
    id: 'serviceTag',
    label: 'Service Name',
    placeholder: 'Enter service name',
    maxLength: 100,
  },
  wasteTypeId: {
    id: 'wasteTypeId',
    label: 'Waste Type',
    placeholder: 'Select waste type',
  },
  binTypeId: {
    id: 'binTypeId',
    label: 'Bin Type (Optional)',
    placeholder: 'Select bin type',
    info: 'You have selected a non-inventory bin type',
  },
  description: {
    id: 'description',
    label: 'Description',
    placeholder: placeholderRows(5, 'Write service description'),
    rows: 5,
  },
  billingType: {
    id: 'billingType',
    label: 'Billing Type',
    placeholder: '',
  },
  price: {
    id: 'price',
    label: 'Rate',
    labelInvoice: 'Invoice',
    labelPurchase: 'Purchase',
    labelExpense: 'Expense',
    placeholder: ' ',
  },
  isTaxInclusive: {
    id: 'isTaxInclusive',
    label: 'Tax Inclusive',
    placeholder: '',
  },
  chargeCategoryId: {
    id: 'chargeCategoryId',
    label: 'Billing Category',
    placeholder: 'Select billing category',
    info: 'Invoice item by weight',
  },
  nonBillableWeight: {
    id: 'nonBillableWeight',
    label: 'Start Billing from',
    placeholder: ' ',
    info: 'Refers to first few weight units that is complementary\nNett Weight - Non billable Weight = Billable Weight',
  },
  
  defaultTaxRate: {
    id: 'defaultTaxRate',
    label: '',
    title: 'Default Tax Rate',
    placeholder: '',
    tooltip: 'Set default tax rate',
    required: 'Tax rate cannot exceed 100%',
  },
  
  isSelectAllSites: {
    id: 'isSelectAllSites',
    label: 'Apply to sites',
    labelTrue: 'All sites',
    labelFalse: 'Selected sites',
    placeholder: ' ',
  },
  autoApplyToNewSites: {
    id: 'autoApplyToNewSites',
    label: 'Auto apply to new sites',
    placeholder: ' ',
  },
  customerSiteId: {
    id: 'customerSiteId',
    label: 'Customer sites',
    placeholder: ' ',
  },
};

export const formSchema = (id: number|null = null, isLocalGlobal: number|null) => {
  return Yup.object().shape({
    uom: Yup.number().nullable().oneOf([ReportUOM.tons, ReportUOM.kg]),
    
    serviceTag: Yup.string().required().label(fields.serviceTag.label),

    hasBinWaste: Yup.bool().oneOf([true, false]),
    wasteTypeId: Yup.number().when(['hasBinWaste'], ([hasBinWaste]) => {
      if(hasBinWaste){
        return Yup.number().nullable().required().label(fields.wasteTypeId.label)
      } else {
        return Yup.number().nullable().label(fields.wasteTypeId.label)
      }
    }),
    binTypeId: Yup.number().nullable().label(fields.binTypeId.label),

    description: Yup.string().label(fields.description.label),
    billingType: Yup.number().nullable().min(0).label(fields.billingType.label),

    hasBillingPermission: Yup.bool().oneOf([true, false]),
    isTaxInclusive: Yup.bool().oneOf([true, false]),
    useBinWeightQuantity: Yup.bool().oneOf([true, false]),

    price: Yup.number().when(['hasBillingPermission'], ([hasBillingPermission]) => {
      if(hasBillingPermission){
        return Yup.number().nullable().required().label(fields.price.label)
      } else {
        return Yup.number().nullable().label(fields.price.label)
      }
    }),

    chargeCategoryId: Yup.number().when(['hasBillingPermission'], ([hasBillingPermission]) => {
      if(hasBillingPermission){
        return Yup.number().nullable().required().label(fields.chargeCategoryId.label)
      } else {
        return Yup.number().nullable().label(fields.chargeCategoryId.label)
      }
    }),

    nonBillableWeight: Yup.number().when(['uom', 'useBinWeightQuantity'], ([uom, useBinWeightQuantity]) => {
      if(useBinWeightQuantity){
        return (uom === ReportUOM.kg
          ? getYupForKG(fields.nonBillableWeight.label)
          : getYupForTONS(fields.nonBillableWeight.label)
        ).max(getYupForMAX(uom, null));
      } else {
        return Yup.number().nullable().min(0).label(fields.nonBillableWeight.label)
      }
    }),

    
    isSelectAllSites: Yup.bool().oneOf([true, false]),
    autoApplyToNewSites: Yup.bool().oneOf([true, false]),
    customerSiteId: Yup.array().when(['isSelectAllSites'], ([isSelectAllSites]) => {
      if(isLocalGlobal === LocalGlobal.LOCAL){
        if(isSelectAllSites){
          return Yup.array().nullable().label(fields.customerSiteId.label);
        } else {
          return Yup.array().min(1).label(fields.customerSiteId.label);
        }
      } else {
        return Yup.array().nullable().label(fields.customerSiteId.label);
      }
    }),


    bundleServices: Yup.array().of(Yup.object().shape({
        serviceTag: Yup.string().required().label(fields.serviceTag.label),
        billingType: Yup.number().nullable().min(0).label(fields.billingType.label),

        hasBillingPermission: Yup.bool().oneOf([true, false]),
        isTaxInclusive: Yup.bool().oneOf([true, false]),
        useBinWeightQuantity: Yup.bool().oneOf([true, false]),

        price: Yup.number().when(['hasBillingPermission'], ([hasBillingPermission]) => {
          if(hasBillingPermission){
            return Yup.number().nullable().required().label(fields.price.label)
          } else {
            return Yup.number().nullable().label(fields.price.label)
          }
        }),
        
        chargeCategoryId: Yup.number().when(['hasBillingPermission'], ([hasBillingPermission]) => {
          if(hasBillingPermission){
            return Yup.number().nullable().required().label(fields.chargeCategoryId.label)
          } else {
            return Yup.number().nullable().label(fields.chargeCategoryId.label)
          }
        }),

        nonBillableWeight: Yup.number().when(['$uom', 'useBinWeightQuantity'], ([uom, useBinWeightQuantity]) => {
          if(useBinWeightQuantity){
            return (uom === ReportUOM.kg
              ? getYupForKG(fields.nonBillableWeight.label)
              : getYupForTONS(fields.nonBillableWeight.label)
            ).max(getYupForMAX(uom, null));
          } else {
            return Yup.number().nullable().min(0).label(fields.nonBillableWeight.label)
          }
        }),
    })),
  })
}


let formikContext: any = null;
export const FormikContext = createContext<any>(null);
export const useFormikContext = () => {
    formikContext = useContext(FormikContext);
    if (!formikContext) {
      throw new Error('useFormikContext must be used within a FormikProvider');
    }
    return formikContext;
};


export const prepareForm = (values: any = null, defValues: any = null, id: any = null, isCopy: boolean = false, isLocalGlobal: number|null) => {
  let form = _.cloneDeep(values);
  let data = _.cloneDeep(defValues);

  if(data && form){
    let wasteType = (form && form.wasteType) ? form.wasteType : null;
    let wasteTypeId = (wasteType && wasteType.wasteTypeId) ? wasteType.wasteTypeId : null;
    let wasteTypeName = (wasteType && wasteType.wasteTypeName) ? wasteType.wasteTypeName : '';

    let binType = (form && form.binType) ? form.binType : null;
    let binTypeId = (binType && binType.binTypeId) ? binType.binTypeId : null;
    let binTypeName = (binType && binType.binTypeName) ? binType.binTypeName : '';

    let chargeCategory = (form && form.chargeCategory) ? form.chargeCategory : null;
    let chargeCategoryId = (chargeCategory && chargeCategory.chargeCategoryId) ? chargeCategory.chargeCategoryId : null;
    let chargeCategoryName = (chargeCategory && chargeCategory.chargeCategoryName) ? chargeCategory.chargeCategoryName : '';

    let bundleServices = (form.bundleServices && form.bundleServices.length > 0)
      ?
      form.bundleServices.sort((a: any, b: any) => a.order - b.order).map((item: any) => {
        let bundleChargeCategory = (item && item.chargeCategory) ? item.chargeCategory : null;
        let bundleChargeCategoryId = (bundleChargeCategory && bundleChargeCategory.chargeCategoryId) ? bundleChargeCategory.chargeCategoryId : null;
        let bundleChargeCategoryName = (bundleChargeCategory && bundleChargeCategory.chargeCategoryName) ? bundleChargeCategory.chargeCategoryName : '';

        let bundleItem: BundleItem = {
          order: item?.order,
          billingType: item.billingType,
          hasBillingPermission: hasBillingPermission(),
          isTaxInclusive: ((item.isTaxInclusive === false) || (item.isTaxInclusive === true)) ? item.isTaxInclusive : false,
          useBinWeightQuantity: item.useBinWeightQuantity,
          serviceItemTemplateId: null,
          serviceTag: item.serviceTag,
          wasteTypeId: null,
          wasteTypeName: '',
          binTypeId: null,
          binTypeName: '',
          description: item?.description,
          price: item.price,
          chargeCategoryId: bundleChargeCategoryId,
          chargeCategoryName: bundleChargeCategoryName,
          chargeCategory: bundleChargeCategory,
          nonBillableWeight: item.nonBillableWeight,
        };
        return bundleItem
      })
    :
    [];

    
    data['uom'] = getBillingUOMv4();
    data['hasBillingPermission'] = hasBillingPermission();
    
    data['isActive'] = ((form.isActive === false) || (form.isActive === true)) ? form.isActive : false;
    data['isTaxInclusive'] = ((form.isTaxInclusive === false) || (form.isTaxInclusive === true)) ? form.isTaxInclusive : false;
    data['useBinWeightQuantity'] = ((form.useBinWeightQuantity === false) || (form.useBinWeightQuantity === true)) ? form.useBinWeightQuantity : false;
    
    data['serviceTag'] = (form && form.serviceTag && form.serviceTag !== '') ? form.serviceTag + (isCopy ? ' - copy' : '') : '';
    data['price'] = (form && form.price && form.price !== '' || form.price == '0') ? form.price : '';
    data['nonBillableWeight'] = (form && form.nonBillableWeight && form.nonBillableWeight !== '' || form.nonBillableWeight == '0') ? form.nonBillableWeight : '';

    data['billingType'] = form.billingType;
    data['description'] = form.description;

    data['hasBinWaste'] = isNumeric(id) ? isNumeric(wasteTypeId) ? true : false : true;
    data['wasteTypeId'] = wasteTypeId;
    data['wasteTypeName'] = wasteTypeName;
    data['wasteType'] = wasteType;
    data['binTypeId'] = binTypeId;
    data['binTypeName'] = binTypeName;
    data['binType'] = binType;
    data['chargeCategoryId'] = chargeCategoryId;
    data['chargeCategoryName'] = chargeCategoryName;
    data['chargeCategory'] = chargeCategory;

    data['bundleServices'] = bundleServices;

    if(isLocalGlobal === LocalGlobal.LOCAL){
      let customerSiteId: Array<any> = [];
      let customSiteName: Array<any> = [];
      if(form.serviceItemCustomerSites && form.serviceItemCustomerSites.length > 0){
        form.serviceItemCustomerSites.forEach((item: any, i: number) => {
          customerSiteId.push(item.customerSiteId);
          customSiteName.push(item?.customSiteName);
        });
      }
      let isSelectAllSites = ((form.isSelectAllSites === false) || (form.isSelectAllSites === true)) ? form.isSelectAllSites : false;
      let autoApplyToNewSites = ((form.autoApplyToNewSites === false) || (form.autoApplyToNewSites === true)) ? form.autoApplyToNewSites : false;
  
      data['customerSiteId'] = customerSiteId;
      data['customSiteName'] = customSiteName;
      data['isSelectAllSites'] = isSelectAllSites;
      data['autoApplyToNewSites'] = isSelectAllSites ? true : autoApplyToNewSites;
    }
  }

  return data;
};
export const prepareData = (values: any = null, customerId: any = null, id: any = null, isCopy: boolean = false, isLocalGlobal: number|null) => {
  let data: any = {};

  if(values){
    let wasteType = null;
    let wasteTypeId = values.wasteTypeId;
    if(wasteTypeId){
      wasteType = {
        wasteTypeId: wasteTypeId,
        wasteTypeName: values.wasteTypeName,
      }
    }

    let binType = null;
    let binTypeId = values.binTypeId;
    if(binTypeId){
      binType = {
        binTypeId: binTypeId,
        binTypeName: values.binTypeName,
      }
    }

    // let chargeCategory = null;
    let chargeCategoryId = values.chargeCategoryId;
    // if(chargeCategoryId){
    //   chargeCategory = {
    //     chargeCategoryId: chargeCategoryId,
    //     chargeCategoryName: values.chargeCategoryName,
    //   }
    // }
    
    let bundleServices = [];
    if(values.bundleServices && values.bundleServices.length > 0){
      bundleServices = values.bundleServices.sort((a: any, b: any) => a.order - b.order).map((item: any, i: number) => {
        item['price'] = (item['price'] != '') ? item['price'] : 0;
        return item;
      })
    }


    data['isActive'] = values.isActive;

    data['serviceTag'] = values.serviceTag;
    data['description'] = values.description;
    data['billingType'] = values.billingType;
    data['useBinWeightQuantity'] = values.useBinWeightQuantity;
    data['nonBillableWeight'] = values.nonBillableWeight;

    data['price'] = (values.price != '') ? values.price : 0;
    data['isTaxInclusive'] = ((values.isTaxInclusive === false) || (values.isTaxInclusive === true)) ? values.isTaxInclusive : false;

    data['wasteTypeId'] = wasteTypeId;
    data['wasteType'] = wasteType;
    data['binTypeId'] = binTypeId;
    data['binType'] = binType;
    data['chargeCategoryId'] = chargeCategoryId;

    data['bundleServices'] = bundleServices;

    
    if(isLocalGlobal === LocalGlobal.LOCAL){
      let serviceItemCustomerSites = [];
      if(values.customerSiteId && values.customerSiteId.length > 0) {
        serviceItemCustomerSites = values.customerSiteId.map((x: any, i: number) => ({
          customerSiteId: x,
        }));
      }
      data['serviceItemCustomerSites'] = serviceItemCustomerSites;
      data['isSelectAllSites'] = values.isSelectAllSites;
      data['autoApplyToNewSites'] = values.autoApplyToNewSites;
    }


    if ((!isCopy) && isNumeric(id)) {
      data['serviceItemId'] = id;
    }
    
    if (isNumeric(customerId)) {
      data['customerId'] = customerId;
    }
  }

  return data;
};

export const hasBillingPermission = () => {
  let state = true;

  let billingInv = hasPermission(Permissions.BillingInvoice)
  let billingPurch = hasPermission(Permissions.BillingPurchase)
  let billingExp = hasPermission(Permissions.BillingExpense)
  let billingCat = hasPermission(Permissions.BillingCategory)

  if ((billingInv == false) && (billingPurch == false) && (billingExp == false) && (billingCat == false)) {
    state = false;
  }

  return state;
};
export const addBillingCategory = (data: any) => {
  if(data){
    let chargeCategory = (data && data.chargeCategory) ? data.chargeCategory : null;
    let chargeCategoryId = (chargeCategory) ? chargeCategory.chargeCategoryId : null;
    let chargeCategoryName = (chargeCategory) ? chargeCategory.chargeCategoryName : '';
    
    let billingType = (data) ? data.billingType : 0;
    let useBinWeightQuantity = (data) ? data.useBinWeightQuantity : false;

    let invoiceTaxRate = (chargeCategory) ? chargeCategory.invoiceTaxRate : null;
    let purchaseTaxRate = (chargeCategory) ? chargeCategory.purchaseTaxRate : null;

    return {
      chargeCategoryId: chargeCategoryId,
      chargeCategoryName: chargeCategoryName,
      chargeCategory: chargeCategory,
      billingType: billingType,
      useBinWeightQuantity: useBinWeightQuantity,
      invoiceTaxRate: invoiceTaxRate,
      purchaseTaxRate: purchaseTaxRate,
    };
  }

  return null;
}
export const getSelectedTaxRate = (billingType: any, taxRateSlice: any) => {
  return taxRateSlice?.isConnected ? (billingType === BillingTypes.Purchase) ? taxRateSlice?.purchaseTaxRate : taxRateSlice?.invoiceTaxRate : taxRateSlice?.defaultTaxRate;
}
export const getBillingPerKgTons = () => {
  return (getBillingUOMv4() == ReportUOM.kg) ? '/ kg' : '/ ton';
}
export const getBillingKgTons = () => {
  return (getBillingUOMv4() == ReportUOM.kg) ? 'kg' : 'tons';
}
export const getMaxLength = (uom: any) => {
  return (uom === ReportUOM.kg) ? 2 : 3;
}


interface BundleItem {
  order: number,
  billingType: number|null,
  hasBillingPermission: boolean,
  isTaxInclusive: boolean,
  useBinWeightQuantity: boolean,
  serviceItemTemplateId: any,
  serviceTag: string|null,
  wasteTypeId: any,
  wasteTypeName: string|null,
  binTypeId: any,
  binTypeName: string|null,
  description: string|null,
  price: string|null,
  chargeCategoryId: any,
  chargeCategoryName: string|null,
  chargeCategory: any,
  nonBillableWeight: string|null,
};
export const initBundleItem: BundleItem = {
  order: 1,
  billingType: null,
  hasBillingPermission: hasBillingPermission(),
  isTaxInclusive: false,
  useBinWeightQuantity: false,
  serviceItemTemplateId: null,
  serviceTag: '',
  wasteTypeId: null,
  wasteTypeName: '',
  binTypeId: null,
  binTypeName: '',
  description: '',
  price: '',
  chargeCategoryId: null,
  chargeCategoryName: '',
  chargeCategory: null,
  nonBillableWeight: '',
};


export interface initialValuesStruct {
  hasBinWaste: boolean,
  uom: number|null,
  billingType: number|null,
  hasBillingPermission: boolean,
  isTaxInclusive: boolean,
  useBinWeightQuantity: boolean,
  serviceTag: string|null,
  wasteTypeId: any,
  wasteTypeName: string|null,
  binTypeId: any,
  binTypeName: string|null,
  description: string|null,
  price: string|null,
  chargeCategoryId: any,
  chargeCategoryName: string|null,
  nonBillableWeight: string|null,

  bundleServices: Array<BundleItem>,
  
  isSelectAllSites?: boolean,
  autoApplyToNewSites?: boolean,
  customerSiteId?: any,
  customSiteName?: any,
};
export const initialValues: initialValuesStruct = {
  hasBinWaste: true,
  uom: getBillingUOMv4(),
  billingType: null,
  hasBillingPermission: hasBillingPermission(),
  isTaxInclusive: false,
  useBinWeightQuantity: false,
  serviceTag: '',
  wasteTypeId: null,
  wasteTypeName: '',
  binTypeId: null,
  binTypeName: '',
  description: '',
  price: '',
  chargeCategoryId: null,
  chargeCategoryName: '',
  nonBillableWeight: '',

  bundleServices: [],

  isSelectAllSites: true,
  autoApplyToNewSites: true,
  customerSiteId: null,
  customSiteName: null,
};


interface InitState {
  isLoading: boolean,

  show: boolean,
  customerId: any|null,
  id: any|null,
  isCopy: boolean,

  items: Array<any>,
  details: any,

  isLoadingCheckName: boolean,
  isLocalGlobal: number|null,
}

function NewReducer() {
  const name = 'servicesSlice';


  const initialState: InitState = {
    isLoading: false,
    show: false,
    customerId: null,
    id: null,
    isCopy: false,

    items: [],
    details: initialValues,

    isLoadingCheckName: false,
    isLocalGlobal: null,
  };


  const reducers = {
    resetSlice: () => {
      return initialState;
    },
    setLoading: (state: InitState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setId: (state: InitState, action: PayloadAction<any>) => {
      state.id = action.payload;
    },
    setShow: (state: InitState, action: PayloadAction<{ show: boolean, customerId: number|null, id: number|null, isCopy: boolean, isLocalGlobal: number|null}>) => {
      state.id = action.payload.id;
      state.customerId = action.payload.customerId;
      state.isCopy = action.payload.isCopy;
      state.isLocalGlobal = action.payload.isLocalGlobal;
      state.show = action.payload.show;
    },
    setValues: (state: InitState, action: PayloadAction<any>) => {
      state.details = action.payload;
    },

    startRead: (state: InitState) => {
      state.isLoading = true;
      state.items = [];
    },
    finishRead: (state: InitState, action: PayloadAction<any>) => {
      let data = (action.payload && action.payload.data && action.payload.data.length > 0) ? action.payload.data : [];
      state.items = data;
      state.isLoading = false;
    },

    startDetails: (state: InitState) => {
      state.isLoading = true;
    },
    finishDetails: (state: InitState, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.details = action.payload;
    },

    startCreate: (state: InitState) => {
      state.isLoading = true;
    },
    finishCreate: (state: InitState, action: PayloadAction<any>) => {
      state.isLoading = false;
    },

    startUpdate: (state: InitState) => {
      state.isLoading = true;
    },
    finishUpdate: (state: InitState, action: PayloadAction<any>) => {
      state.isLoading = false;
    },

    startDelete: (state: InitState) => {
      state.isLoading = true;
    },
    finishDelete: (state: InitState, action: PayloadAction<any>) => {
      state.isLoading = false;
    },

    startCheckName: (state: InitState) => {
      state.isLoadingCheckName = true;
    },
    finishCheckName: (state: InitState, action: PayloadAction<any>) => {
      state.isLoadingCheckName = false;
    },
    
    startCheckBillingCategory: (state: InitState) => {
      state.isLoading = true;
    },
    finishCheckBillingCategory: (state: InitState, action: PayloadAction<any>) => {
      state.isLoading = false;
    },
  };


  const apis = {
    callReadApi: (params: any, callback: (state: boolean, data: any) => void) => async (dispatch: any) => {
      dispatch(actions.startRead());

      await axios.get('service-item', { params: params }).then((result: any) => {
          let data = result.data;
          
          successAPI(data);

          callback(true, data);
          dispatch(actions.finishRead(data));
      }).catch((error: any) => {
          errorAPI(error);
          
          callback(false, null);
          dispatch(actions.finishRead(null));
      });
    },

    callDetailsApi: (id: number|null, callback: (state: boolean, data: any) => void) => async (dispatch: any) => {
      dispatch(actions.startDetails());

      await axios.get('service-item/' + id).then((result: any) => {
          let data = result.data;
          
          successAPI(data);

          callback(true, data);
          dispatch(actions.finishDetails(data));
      }).catch((error: any) => {
          errorAPI(error);
          
          callback(false, null);
          dispatch(actions.finishDetails(null));
      });
    },

    callCreateApi: (params: any, callback: (state: boolean, data: any) => void) => async (dispatch: any) => {
      dispatch(actions.startCreate());

      await axios.post('service-item', params).then((result: any) => {
          let data = result.data;
          
          // successAPI(data);
          // success('Saved successfully', 'The data you entered has been successfully saved.');

          callback(true, data);
          dispatch(actions.finishCreate(data));
      }).catch((error: any) => {
          errorAPI(error);
          
          callback(false, null);
          dispatch(actions.finishCreate(null));
      });
    },

    callUpdateApi: (params: any, callback: (state: boolean, data: any) => void) => async (dispatch: any) => {
      dispatch(actions.startUpdate());

      await axios.put('service-item', params).then((result: any) => {
          let data = result.data;
          
          // successAPI(data);
          // success('Updated successfully', 'Your data has been successfully updated.');

          callback(true, data);
          dispatch(actions.finishUpdate(data));
      }).catch((error: any) => {
          errorAPI(error);
          
          callback(false, null);
          dispatch(actions.finishUpdate(null));
      });
    },

    callDeleteApi: (params: any, callback: (state: boolean, data: any) => void) => async (dispatch: any) => {
      dispatch(actions.startDelete());

      await axios.delete('service-item', { data: params }).then((result: any) => {
          let data = result.data;
              
          // successAPI(data);
          success('Deleted successfully', 'Job Type has been successfully deleted.');

          callback(true, data);
          dispatch(actions.finishDelete(data));
      }).catch((error: any) => {
          errorAPI(error);
          
          callback(false, null);
          dispatch(actions.finishDelete(null));
      });
    },

    callCheckNameApi: (params: any, callback: (state: boolean, data: any) => void) => async (dispatch: any) => {
      dispatch(actions.startCheckName());

      await axios.post('service-item/check-name', params).then((result: any) => {
          let data = result.data;
          
          // successAPI(data);

          callback(true, data);
          dispatch(actions.finishCheckName(data));
      }).catch((error: any) => {
          errorAPI(error);
          
          callback(false, null);
          dispatch(actions.finishCheckName(null));
      });
    },
    
    callCheckBillingCategoryApi: (params: any, callback: (state: boolean, data: any) => void) => async (dispatch: any) => {
      dispatch(actions.startCheckBillingCategory());

      await axios.get('service-item/query', { params: params }).then((result: any) => {
          let data = result.data;
          
          // successAPI(data);

          callback(true, data);
          dispatch(actions.finishCheckBillingCategory(data));
      }).catch((error: any) => {
          errorAPI(error);
          
          callback(false, null);
          dispatch(actions.finishCheckBillingCategory(null));
      });
    },
  };


  const { reducer, actions } = createSlice({
    name,
    initialState,
    reducers,
  });


  return {
    reducer,
    ...actions,
    ...apis,
  };
}


export default NewReducer();